class WLSAnchorPills extends HTMLElement {
    constructor() {
        super();

        this.classes = {
            sticky: 'sticky',
            section: 'shopify-section',
            active: 'active',
            pill: 'wls-anchor-pills__pill'
        };

        this.elements = {
            section: this.closest(`.${this.classes.section}`),
            sentinel: null,
            pills: this.querySelectorAll(`.${this.classes.pill}`)
        };
    }

    /**
     * Initialize when element is added to DOM
     */
    connectedCallback() {
        this.init();
    }

    /**
     * Clean up when element is removed from DOM
     */
    disconnectedCallback() {
        if (this.stickyObserver) {
            this.stickyObserver.disconnect();
        }
        if (this.sectionObserver) {
            this.sectionObserver.disconnect();
        }
        if (this.elements.sentinel) {
            this.elements.sentinel.remove();
        }
    }

    /**
     * Initialize observers
     */
    init() {
        if (!this.elements.section) return;

        this.initStickyObserver();
        this.initSectionObserver();
        this.checkInitialVisibleSection();
    }

    /**
     * Initialize sticky observer with a sentinel element
     */
    initStickyObserver() {
        // Create and insert sentinel element
        this.elements.sentinel = document.createElement('div');
        this.elements.sentinel.style.cssText = 'position: absolute; height: 1px; width: 1px; opacity: 0;';
        this.elements.section.parentElement.insertBefore(this.elements.sentinel, this.elements.section);

        const options = {
            threshold: [0],
            rootMargin: "0px 0px 0px 0px"
        };

        this.stickyObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) {
                    if (!this.elements.section.classList.contains(this.classes.sticky)) {
                        this.elements.section.classList.add(this.classes.sticky);
                    }
                } else {
                    if (this.elements.section.classList.contains(this.classes.sticky)) {
                        this.elements.section.classList.remove(this.classes.sticky);
                    }
                }
            });
        }, options);

        this.stickyObserver.observe(this.elements.sentinel);
    }

    /**
     * Initialize section observer for pills
     */
    initSectionObserver() {
        if (!this.elements.pills.length) return;

        const options = {
            root: null,
            rootMargin: "-30% 0px -30% 0px",
            threshold: 0
        };

        this.sectionObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const pill = this.querySelector(`.${this.classes.pill}[href="#${entry.target.id}"]`);
                if (!pill) return;

                if (entry.isIntersecting) {
                    // Remove active class from all pills
                    this.elements.pills.forEach(p => p.classList.remove(this.classes.active));
                    // Add active class to current pill
                    pill.classList.add(this.classes.active);
                    this.scrollPillIntoView(pill);
                }
            });
        }, options);

        // Start observing sections referenced by pills
        this.elements.pills.forEach(pill => {
            const href = pill.getAttribute('href');
            if (!href?.startsWith('#')) return;

            const sectionId = href.substring(1);
            const section = document.getElementById(sectionId);
            if (section) {
                this.sectionObserver.observe(section);
            }
        });
    }

    /**
     * Check which section is initially visible and set its pill as active
     */
    checkInitialVisibleSection() {
        if (!this.elements.pills.length) return;

        // Get all sections
        const sections = Array.from(this.elements.pills).map(pill => {
            const href = pill.getAttribute('href');
            if (!href?.startsWith('#')) return null;
            return document.getElementById(href.substring(1));
        }).filter(Boolean);

        // Find first visible section
        const visibleSection = sections.find(section => {
            const rect = section.getBoundingClientRect();
            // Section is visible if it's in viewport
            return rect.top < window.innerHeight && rect.bottom > 0;
        });

        // Remove active class from all pills first
        this.elements.pills.forEach(pill => pill.classList.remove(this.classes.active));

        if (visibleSection) {
            // Add active class to pill of visible section
            const pill = this.querySelector(`.${this.classes.pill}[href="#${visibleSection.id}"]`);
            if (pill) {
                pill.classList.add(this.classes.active);
                this.scrollPillIntoView(pill);
            }
        } else {
            // Fallback to first pill if no section is visible
            const firstPill = this.elements.pills[0];
            if (firstPill) {
                firstPill.classList.add(this.classes.active);
                this.scrollPillIntoView(firstPill);
            }
        }
    }

    /**
     * Scroll active pill into center view
     * @param {HTMLElement} pill - The pill element to center
     */
    scrollPillIntoView(pill) {
        const pillsContainer = pill.parentElement;
        if (!pillsContainer) return;

        const containerWidth = pillsContainer.offsetWidth;
        const pillLeft = pill.offsetLeft;
        const pillWidth = pill.offsetWidth;
        
        // Calculate the scroll position that will center the pill
        const scrollPosition = pillLeft - (containerWidth / 2) + (pillWidth / 2);
        
        pillsContainer.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
        });
    }
}

// Prevent double registration
if (!customElements.get('wls-anchor-pills')) {
    customElements.define('wls-anchor-pills', WLSAnchorPills);
}
